import React, { useEffect, useState } from 'react';
import {
  StyleSheet,
  View,
  Text,
  ScrollView,
  ActivityIndicator,
  TouchableOpacity,
  RefreshControl,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { getRecentActivity, RecentActivity } from '../../api/activityLog';
import theme from '../../theme';
import { useNavigation } from '@react-navigation/native';

export const ActivityLogScreen: React.FC = () => {
  const navigation = useNavigation();
  const [activities, setActivities] = useState<RecentActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'create' | 'update' | 'delete' | 'status_change'>('all');

  useEffect(() => {
    loadActivities();
  }, [filter]);

  const loadActivities = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getRecentActivity(100); // Get last 100 activities
      
      if (result.error || !result.data) {
        setError(result.error || 'Failed to load activity log');
        setActivities([]);
        return;
      }
      
      // Filter by action type if needed
      const filtered = filter === 'all' 
        ? result.data 
        : result.data.filter((a: any) => a.action_type === filter);
      
      setActivities(filtered);
    } catch (err) {
      console.error('Error loading activities:', err);
      setError('Failed to load activity log');
    } finally {
      setLoading(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadActivities();
    setRefreshing(false);
  };

  const getActionIcon = (actionType: string) => {
    switch (actionType) {
      case 'create': return 'add-circle';
      case 'update': return 'create';
      case 'delete': return 'trash';
      case 'status_change': return 'swap-horizontal';
      case 'form_submission': return 'document-text';
      default: return 'information-circle';
    }
  };

  const getActionColor = (actionType: string) => {
    switch (actionType) {
      case 'create': return theme.colors.success;
      case 'update': return theme.colors.primary;
      case 'delete': return theme.colors.error;
      case 'status_change': return theme.colors.warning;
      case 'form_submission': return theme.colors.info;
      default: return theme.colors.gray500;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const renderChangeDetails = (changes: any) => {
    if (!changes) return null;

    return (
      <View style={styles.changesContainer}>
        <Text style={styles.changesTitle}>Changes:</Text>
        {Object.entries(changes).map(([key, value]: [string, any]) => (
          <View key={key} style={styles.changeRow}>
            <Text style={styles.changeKey}>{key}:</Text>
            <View style={styles.changeValues}>
              <Text style={styles.changeOldValue}>
                {JSON.stringify(value.before)}
              </Text>
              <Ionicons name="arrow-forward" size={14} color={theme.colors.gray400} />
              <Text style={styles.changeNewValue}>
                {JSON.stringify(value.after)}
              </Text>
            </View>
          </View>
        ))}
      </View>
    );
  };

  const renderFilters = () => (
    <View style={styles.filtersContainer}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        {[
          { label: 'All', value: 'all' },
          { label: 'Created', value: 'create' },
          { label: 'Updated', value: 'update' },
          { label: 'Deleted', value: 'delete' },
          { label: 'Status', value: 'status_change' },
        ].map((item) => (
          <TouchableOpacity
            key={item.value}
            style={[
              styles.filterChip,
              filter === item.value && styles.filterChipActive,
            ]}
            onPress={() => setFilter(item.value as any)}
          >
            <Text
              style={[
                styles.filterChipText,
                filter === item.value && styles.filterChipTextActive,
              ]}
            >
              {item.label}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  const renderTableHeader = () => (
    <View style={styles.tableHeader}>
      <Text style={[styles.tableHeaderText, { flex: 1.5 }]}>Timestamp</Text>
      <Text style={[styles.tableHeaderText, { flex: 1 }]}>Action</Text>
      <Text style={[styles.tableHeaderText, { flex: 1 }]}>Entity</Text>
      <Text style={[styles.tableHeaderText, { flex: 2 }]}>Description</Text>
      <Text style={[styles.tableHeaderText, { flex: 1 }]}>User</Text>
      <View style={{ width: 40 }} />
    </View>
  );

  const renderTableRow = (activity: RecentActivity) => {
    const isExpanded = expandedId === activity.id;
    const actionColor = getActionColor(activity.action_type);

    return (
      <View key={activity.id} style={styles.tableRowContainer}>
        <TouchableOpacity
          style={styles.tableRow}
          onPress={() => setExpandedId(isExpanded ? null : activity.id)}
        >
          <Text style={[styles.tableCellText, { flex: 1.5 }]}>
            {formatTimestamp(activity.created_at)}
          </Text>
          <View style={[styles.tableCell, { flex: 1 }]}>
            <View style={[styles.actionBadge, { backgroundColor: actionColor + '20' }]}>
              <Ionicons name={getActionIcon(activity.action_type) as any} size={14} color={actionColor} />
              <Text style={[styles.actionBadgeText, { color: actionColor }]}>
                {activity.action_type}
              </Text>
            </View>
          </View>
          <Text style={[styles.tableCellText, { flex: 1 }]}>
            {activity.entity_type}
          </Text>
          <Text style={[styles.tableCellText, { flex: 2 }]} numberOfLines={isExpanded ? undefined : 2}>
            {activity.description || 'No description'}
          </Text>
          <Text style={[styles.tableCellText, { flex: 1 }]} numberOfLines={1}>
            {activity.user_email || activity.user_id?.substring(0, 8) || 'System'}
          </Text>
          <Ionicons
            name={isExpanded ? 'chevron-up' : 'chevron-down'}
            size={20}
            color={theme.colors.gray400}
          />
        </TouchableOpacity>

        {isExpanded && activity.changes && (
          <View style={styles.expandedContent}>
            {renderChangeDetails(activity.changes)}
          </View>
        )}
      </View>
    );
  };

  if (loading && !refreshing) {
    return (
      <View style={styles.centerContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={styles.loadingText}>Loading activity log...</Text>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.centerContainer}>
        <Ionicons name="alert-circle" size={48} color={theme.colors.error} />
        <Text style={styles.errorText}>{error}</Text>
        <TouchableOpacity style={styles.retryButton} onPress={loadActivities}>
          <Text style={styles.retryButtonText}>Retry</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
          <Ionicons name="arrow-back" size={24} color={theme.colors.textPrimary} />
        </TouchableOpacity>
        <View style={styles.headerTextContainer}>
          <Text style={styles.headerTitle}>سجل النشاط / Activity Log</Text>
          <Text style={styles.headerSubtitle}>
            {activities.length} {activities.length === 1 ? 'entry' : 'entries'}
          </Text>
        </View>
      </View>

      {renderFilters()}

      <ScrollView
        style={styles.content}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        {Platform.OS === 'web' ? (
          <View style={styles.tableContainer}>
            {renderTableHeader()}
            {activities.map(renderTableRow)}
          </View>
        ) : (
          // Mobile: Card view
          <View style={styles.cardsContainer}>
            {activities.map((activity) => {
              const isExpanded = expandedId === activity.id;
              const actionColor = getActionColor(activity.action_type);

              return (
                <TouchableOpacity
                  key={activity.id}
                  style={styles.card}
                  onPress={() => setExpandedId(isExpanded ? null : activity.id)}
                >
                  <View style={styles.cardHeader}>
                    <View style={[styles.actionBadge, { backgroundColor: actionColor + '20' }]}>
                      <Ionicons name={getActionIcon(activity.action_type) as any} size={16} color={actionColor} />
                      <Text style={[styles.actionBadgeText, { color: actionColor }]}>
                        {activity.action_type}
                      </Text>
                    </View>
                    <Text style={styles.timestamp}>{formatTimestamp(activity.created_at)}</Text>
                  </View>

                  <Text style={styles.cardEntity}>{activity.entity_type}</Text>
                  <Text style={styles.cardDescription}>{activity.description || 'No description'}</Text>

                  <View style={styles.cardFooter}>
                    <Text style={styles.cardUser}>
                      {activity.user_email || activity.user_id?.substring(0, 8) || 'System'}
                    </Text>
                    <Ionicons
                      name={isExpanded ? 'chevron-up' : 'chevron-down'}
                      size={20}
                      color={theme.colors.gray400}
                    />
                  </View>

                  {isExpanded && activity.changes && (
                    <View style={styles.expandedContent}>
                      {renderChangeDetails(activity.changes)}
                    </View>
                  )}
                </TouchableOpacity>
              );
            })}
          </View>
        )}

        {activities.length === 0 && (
          <View style={styles.emptyContainer}>
            <Ionicons name="document-text-outline" size={64} color={theme.colors.gray400} />
            <Text style={styles.emptyText}>No activity log entries found</Text>
          </View>
        )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.gray50,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  backButton: {
    padding: 8,
    marginRight: 8,
  },
  headerTextContainer: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: theme.colors.textPrimary,
  },
  headerSubtitle: {
    fontSize: 14,
    color: theme.colors.gray600,
    marginTop: 4,
  },
  filtersContainer: {
    backgroundColor: theme.colors.white,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  filterChip: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: theme.colors.gray100,
    marginRight: 8,
  },
  filterChipActive: {
    backgroundColor: theme.colors.primary,
  },
  filterChipText: {
    fontSize: 14,
    color: theme.colors.gray600,
    fontWeight: '500',
  },
  filterChipTextActive: {
    color: theme.colors.white,
  },
  content: {
    flex: 1,
  },
  // Table styles (web)
  tableContainer: {
    backgroundColor: theme.colors.white,
    margin: 16,
    borderRadius: 12,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: theme.colors.gray100,
    padding: 12,
    borderBottomWidth: 2,
    borderBottomColor: theme.colors.border,
  },
  tableHeaderText: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.textPrimary,
    paddingHorizontal: 8,
  },
  tableRowContainer: {
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  tableRow: {
    flexDirection: 'row',
    padding: 12,
    alignItems: 'center',
  },
  tableCell: {
    paddingHorizontal: 8,
  },
  tableCellText: {
    fontSize: 14,
    color: theme.colors.textPrimary,
    paddingHorizontal: 8,
  },
  actionBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    gap: 4,
  },
  actionBadgeText: {
    fontSize: 12,
    fontWeight: '600',
  },
  expandedContent: {
    padding: 16,
    backgroundColor: theme.colors.gray50,
  },
  changesContainer: {
    gap: 8,
  },
  changesTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.textPrimary,
    marginBottom: 8,
  },
  changeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  changeKey: {
    fontSize: 13,
    fontWeight: '500',
    color: theme.colors.gray600,
    minWidth: 120,
  },
  changeValues: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  changeOldValue: {
    fontSize: 13,
    color: theme.colors.error,
    textDecorationLine: 'line-through',
  },
  changeNewValue: {
    fontSize: 13,
    color: theme.colors.success,
    fontWeight: '500',
  },
  // Card styles (mobile)
  cardsContainer: {
    padding: 16,
    gap: 12,
  },
  card: {
    backgroundColor: theme.colors.white,
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  cardEntity: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.textPrimary,
    marginBottom: 4,
  },
  cardDescription: {
    fontSize: 14,
    color: theme.colors.gray600,
    marginBottom: 12,
  },
  cardFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  cardUser: {
    fontSize: 13,
    color: theme.colors.gray500,
  },
  timestamp: {
    fontSize: 12,
    color: theme.colors.gray500,
  },
  // Empty state
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
  },
  emptyText: {
    fontSize: 16,
    color: theme.colors.gray600,
    marginTop: 16,
  },
  // Loading & Error
  loadingText: {
    fontSize: 16,
    color: theme.colors.gray600,
    marginTop: 16,
  },
  errorText: {
    fontSize: 16,
    color: theme.colors.error,
    marginTop: 16,
    textAlign: 'center',
  },
  retryButton: {
    marginTop: 16,
    paddingHorizontal: 24,
    paddingVertical: 12,
    backgroundColor: theme.colors.primary,
    borderRadius: 8,
  },
  retryButtonText: {
    color: theme.colors.white,
    fontSize: 16,
    fontWeight: '600',
  },
});
